/**
 * @file ToastContext.jsx
 * @description Global context for managing toast notifications.
 * Provides functions to show and remove toasts with Apple-inspired feedback.
 * 
 * @author Antigravity
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

/**
 * ToastProvider component that wraps the application.
 * Manages the state of active toasts.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    /**
     * Adds a new toast to the stack.
     * 
     * @param {string} message - The message to display
     * @param {string} type - Toast type ('success', 'error', 'warning', 'info')
     * @param {number} [duration=4000] - Display duration in ms
     * @param {Object} [action] - Optional action button
     * @param {string} action.label - Button label
     * @param {Function} action.onClick - Click handler
     */
    const showToast = useCallback((message, type = 'info', duration = 4000, action = null) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type, duration, action }]);
    }, []);

    /**
     * Removes a toast by its ID.
     * 
     * @param {number|string} id - The ID of the toast to remove
     */
    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
            {children}
        </ToastContext.Provider>
    );
};

/**
 * Custom hook to use the toast system.
 * 
 * @returns {Object} { showToast, removeToast }
 * @throws {Error} If used outside of a ToastProvider
 */
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
